import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import AnimatedCounter from "../components/AnimatedCounter";
import {
  Sparkles, HandHeart, Heart, Users, Utensils, Briefcase, GraduationCap,
  ArrowRight, Globe, ShieldCheck, TrendingUp, AlertTriangle, Cpu, Network,
  Handshake, Rocket, Quote, Award,
} from "lucide-react";
import { stats } from "../lib/mockData";
import { problemStats, bigImpact, successStories, futureVisionMilestones } from "../lib/storyData";

const features = [
  { icon: HandHeart, title: "Post a Need", desc: "Ask for help — food, transport, tutoring, anything.", to: "/post-need", color: "bg-primary/10 text-primary" },
  { icon: Heart, title: "Offer Help", desc: "Share skills, time or resources with your neighbors.", to: "/offer-help", color: "bg-secondary/15 text-secondary" },
  { icon: Users, title: "Volunteer", desc: "Join causes near you or contribute remotely.", to: "/volunteer", color: "bg-primary/10 text-primary" },
  { icon: Briefcase, title: "Jobs & Gigs", desc: "Find fair work and short-term opportunities.", to: "/jobs", color: "bg-secondary/15 text-secondary" },
  { icon: Utensils, title: "Food Support", desc: "Give or receive meals through community kitchens.", to: "/food", color: "bg-primary/10 text-primary" },
  { icon: GraduationCap, title: "Mentorship", desc: "Guidance from real people who've walked the path.", to: "/mentorship", color: "bg-secondary/15 text-secondary" },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="noise-bg">
      {/* ============ HERO ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-4 py-1.5 text-xs font-semibold tracking-wide uppercase mb-6">
              <Award size={12} /> Global Humanitarian Innovation · 2026
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-[5.5rem] font-bold tracking-tight text-foreground leading-[1.02] text-balance">
              Communities solving their own problems through <span className="text-primary">AI-powered</span> <span className="text-secondary">human connection</span>.
            </h1>
            <p className="mt-7 text-lg text-muted-foreground max-w-xl leading-relaxed">
              NeighborNet AI is the world's first platform where every neighborhood becomes a self-healing network — matching those who need help with those ready to give it, in real time, across food, jobs, mentorship and emergencies.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button
                onClick={() => navigate("/pitch")}
                data-testid="hero-pitch-btn"
                size="lg"
                className="rounded-full bg-primary hover:bg-primary/90 px-7 h-12 shadow-sm hover:-translate-y-0.5 transition-all"
              >
                Watch the pitch <ArrowRight size={16} className="ml-1" />
              </Button>
              <Button
                onClick={() => navigate("/signup")}
                data-testid="hero-cta-primary"
                variant="outline"
                size="lg"
                className="rounded-full h-12 px-7 border-border hover:bg-muted"
              >
                Join the Net
              </Button>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-secondary" /> Verified profiles</div>
              <div className="flex items-center gap-2"><Globe size={16} className="text-primary" /> {stats.countries} countries</div>
              <div className="flex items-center gap-2"><TrendingUp size={16} className="text-secondary" /> {stats.matchesMade.toLocaleString()}+ matches</div>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up stagger-2">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1517456793572-1d8efd6dc135"
                alt="Diverse community gathering"
                className="w-full h-[520px] object-cover rounded-[2rem] shadow-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-3xl border border-border/60 shadow-lg p-5 max-w-[260px] hidden sm:block">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 rounded-full bg-secondary/20 text-secondary flex items-center justify-center">
                    <HandHeart size={18} />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Match found</div>
                    <div className="text-sm font-display font-semibold">3 min ago</div>
                  </div>
                </div>
                <p className="text-sm text-foreground leading-snug">
                  Amina offered a ride to a dialysis appointment near you.
                </p>
              </div>
              <div className="absolute -top-6 -right-6 bg-white rounded-2xl border border-border/60 shadow-lg px-4 py-3 hidden sm:flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <Sparkles size={14} />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Match confidence</div>
                  <div className="text-lg font-display font-bold text-primary">96%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PROBLEM ============ */}
      <section className="bg-foreground text-background py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-16">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 text-white px-4 py-1.5 text-xs font-semibold tracking-wider uppercase mb-6">
              <AlertTriangle size={12} /> The problem
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-balance leading-[1.05]">
              A silent global crisis is <span className="text-secondary">unfolding</span> in every neighborhood.
            </h2>
            <p className="mt-6 text-lg text-background/70 max-w-2xl leading-relaxed">
              Traditional safety nets are failing. Governments are stretched. NGOs are underfunded. But the biggest untapped resource — <em>people helping people</em> — has no infrastructure to scale.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {problemStats.map((p, i) => (
              <div
                key={p.label}
                data-testid={`problem-stat-${i}`}
                className={`fade-up stagger-${i + 1} rounded-3xl bg-white/5 border border-white/10 p-8 backdrop-blur-sm`}
              >
                <div className="text-6xl font-display font-bold text-secondary tracking-tight">
                  <AnimatedCounter end={p.value} suffix={p.suffix} duration={1800} />
                </div>
                <div className="mt-3 font-display text-xl text-white font-semibold">{p.label}</div>
                <p className="mt-2 text-sm text-background/70 leading-relaxed">{p.desc}</p>
                <div className="mt-5 pt-4 border-t border-white/10 text-[10px] uppercase tracking-wider text-background/50">
                  {p.source}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SOLUTION ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <img
              src="https://images.pexels.com/photos/7156158/pexels-photo-7156158.jpeg"
              alt="Volunteers packing food boxes"
              className="w-full h-[460px] object-cover rounded-[2rem]"
            />
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-secondary/15 text-secondary px-4 py-1.5 text-xs font-semibold tracking-wider uppercase mb-6">
              <Cpu size={12} /> The solution
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-foreground leading-[1.05] text-balance">
              Every block becomes a <span className="text-primary">self-healing network</span>.
            </h2>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed max-w-xl">
              NeighborNet AI turns latent kindness into scaled action. Neighbors post needs and offers; our AI matches them by proximity, skill, availability, and trust — in seconds.
            </p>
            <div className="mt-8 space-y-4">
              {[
                { icon: Network, title: "Hyper-local mesh graph", desc: "Every user is a node. Every kindness strengthens the network for the next person." },
                { icon: Cpu, title: "AI matching engine", desc: "Beyond keywords — we weigh urgency, distance, past reliability, and shared context." },
                { icon: Handshake, title: "Trust built in", desc: "Verified identities, community endorsements, and transparent reviews at every step." },
              ].map((s, i) => (
                <div key={s.title} className={`fade-up stagger-${i + 1} flex gap-4`}>
                  <div className="h-11 w-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <s.icon size={20} />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-lg">{s.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ ANIMATED IMPACT ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="rounded-[2rem] bg-gradient-to-br from-primary via-primary/95 to-primary/80 text-primary-foreground p-8 sm:p-16 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-secondary/25 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
          <div className="relative">
            <div className="max-w-2xl mb-12">
              <Badge className="bg-white/15 text-white border-0 rounded-full mb-4">Our traction</Badge>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-balance leading-[1.05]">
                Real impact. Real numbers. Real lives.
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
              {bigImpact.map((b, i) => (
                <div key={b.key} data-testid={`impact-${b.key}`} className={`fade-up stagger-${i + 1}`}>
                  <div className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
                    <AnimatedCounter end={b.end} suffix={b.suffix} duration={2200} />
                  </div>
                  <div className="text-sm mt-2 text-primary-foreground/85">{b.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ SUCCESS STORIES ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <div className="max-w-2xl mb-14">
          <Badge className="bg-accent text-accent-foreground border-0 rounded-full mb-4">Real people. Real stories.</Badge>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] text-balance">
            Behind every match, a <span className="text-secondary">life changed</span>.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {successStories.map((s, i) => (
            <div
              key={s.id}
              data-testid={`story-${s.id}`}
              className={`fade-up stagger-${i + 1} group rounded-3xl bg-white border border-border/60 overflow-hidden hover:shadow-lg transition-shadow`}
            >
              <div className="h-56 overflow-hidden">
                <img src={s.image} alt={s.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-7">
                <Quote size={22} className="text-primary/50 mb-3" />
                <p className="text-foreground leading-relaxed">"{s.quote}"</p>
                <div className="mt-6 pt-5 border-t border-border/60">
                  <div className="font-display font-semibold">{s.name}</div>
                  <div className="text-sm text-muted-foreground">{s.role}</div>
                  <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-secondary bg-secondary/10 px-3 py-1 rounded-full">
                    <Sparkles size={11} /> {s.outcome}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ WHY WE WIN TOGETHER ============ */}
      <section className="bg-accent/40 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Badge className="bg-primary/10 text-primary border-0 rounded-full mb-4">Our philosophy</Badge>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02] text-balance">
              Why <span className="text-primary">Communities Win Together</span>.
            </h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Societies grow through <em>collaboration, mentorship and collective progress</em> — not isolated success. The most resilient nations in history — from Icelandic fishing villages to Kerala's mutual-aid networks — thrived because neighbors showed up for each other. Modern individualism broke that fabric. AI can weave it back.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                { t: "We win when families thrive.", d: "Not when engagement metrics go up." },
                { t: "We measure lives touched.", d: "Not clicks, not ads, not time-on-site." },
                { t: "Local wisdom scales.", d: "AI amplifies what neighborhoods already know." },
                { t: "Trust is the operating system.", d: "Every match strengthens community fabric." },
              ].map((v, i) => (
                <div key={v.t} className={`fade-up stagger-${i + 1} rounded-2xl bg-white border border-border/60 p-5`}>
                  <div className="font-display font-semibold">{v.t}</div>
                  <div className="text-sm text-muted-foreground mt-1">{v.d}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src="https://images.pexels.com/photos/6647015/pexels-photo-6647015.jpeg"
              alt="Volunteers moving supplies together"
              className="w-full h-[500px] object-cover rounded-[2rem]"
            />
          </div>
        </div>
      </section>

      {/* ============ FOUNDER & VISION (concise) ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="rounded-[2rem] bg-white border border-border/60 p-8 sm:p-12 lg:p-14 overflow-hidden">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-4 mb-8">
                <div
                  data-testid="landing-founder-headshot"
                  aria-label="Founder Adebowale Jimoh"
                  className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-secondary text-white font-display font-bold text-2xl flex items-center justify-center shadow-md ring-4 ring-primary/10 shrink-0"
                >
                  AJ
                </div>
                <div>
                  <Badge className="bg-primary/10 text-primary border-0 rounded-full mb-1">Founder</Badge>
                  <div className="font-display text-2xl font-bold tracking-tight">Adebowale Jimoh</div>
                  <div className="text-xs text-muted-foreground">Founder · NeighborNet AI</div>
                </div>
              </div>

              <div className="rounded-3xl overflow-hidden border border-border/60 bg-muted/30">
                <img
                  src="https://customer-assets.emergentagent.com/job_neighborai-hub/artifacts/aia5aupa_IMG-20260312-WA0001.jpg"
                  alt="One Naira Movement — Adebowale's humanitarian work that inspired NeighborNet AI"
                  data-testid="founder-inspiration-image"
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
              <div className="mt-4 text-xs text-muted-foreground text-center italic">
                The One Naira Movement — the real-world humanitarian work that inspired NeighborNet AI.
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-4">
                Vision
              </div>
              <Quote size={30} className="text-primary/30 mb-3" />
              <blockquote className="font-display text-2xl sm:text-3xl leading-[1.35] text-foreground text-balance italic">
                "NeighborNet AI was created to empower communities to solve problems collectively through technology, compassion, and shared responsibility."
              </blockquote>
              <div className="mt-8 h-px bg-border" />
              <p className="mt-6 text-muted-foreground leading-relaxed max-w-2xl">
                A global social impact platform built on the belief that the strongest societies are the ones where neighbors show up for each other — with technology as an amplifier, never a replacement, for human kindness.
              </p>
              <p className="mt-4 text-muted-foreground leading-relaxed max-w-2xl">
                From restoring smiles on the streets of Lagos to matching mentors across continents — NeighborNet AI carries that same mission, now at planetary scale.
              </p>

              <div className="mt-8 rounded-2xl bg-secondary/10 border border-secondary/20 p-5" data-testid="sister-initiative-note">
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-xl bg-secondary/20 text-secondary flex items-center justify-center shrink-0">
                    <Sparkles size={14} />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-foreground">A sister initiative</div>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                      <span className="font-semibold text-foreground">One Naira Movement</span> is the sister initiative of NeighborNet AI — a grassroots Nigerian charity restoring smiles and dignity to vulnerable children on the ground. NeighborNet AI is the digital layer built on top of that same conviction: every neighbor, everywhere, deserves someone to show up for them.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FUTURE VISION ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <div className="max-w-3xl mb-16">
          <Badge className="bg-secondary/15 text-secondary border-0 rounded-full mb-4">The road ahead</Badge>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] text-balance">
            From a block, to a city, to <span className="text-secondary">the world</span>.
          </h2>
          <p className="mt-5 text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Our roadmap to becoming the default social infrastructure for how humanity helps itself.
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute left-0 right-0 top-6 h-0.5 bg-border" />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            {futureVisionMilestones.map((m, i) => (
              <div
                key={m.year}
                data-testid={`vision-${m.year}`}
                className={`fade-up stagger-${i + 1} relative`}
              >
                <div className="hidden lg:block absolute -top-1 left-0 h-3 w-3 rounded-full bg-primary ring-4 ring-background" />
                <div className="lg:pt-12 rounded-3xl bg-white border border-border/60 p-6 h-full">
                  <div className="text-3xl font-display font-bold text-primary">{m.year}</div>
                  <h3 className="mt-2 font-display font-semibold text-lg">{m.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FEATURES QUICK LINKS ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
        <div className="max-w-2xl mb-12">
          <Badge className="bg-accent text-accent-foreground border-0 rounded-full mb-4">What you can do</Badge>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground tracking-tight">
            Six ways your community shows up for each other.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <Link
              key={f.title}
              to={f.to}
              data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`fade-up stagger-${(i % 6) + 1} group rounded-3xl bg-white border border-border/60 p-7 hover:border-primary/40 hover:shadow-md transition-all`}
            >
              <div className={`h-12 w-12 rounded-2xl ${f.color} flex items-center justify-center mb-5`}>
                <f.icon size={20} strokeWidth={2} />
              </div>
              <h3 className="font-display text-xl font-semibold text-foreground mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-sm text-primary font-medium group-hover:gap-2 transition-all">
                Explore <ArrowRight size={14} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="rounded-[2rem] bg-gradient-to-br from-secondary/25 via-white to-primary/15 border border-border/60 p-10 sm:p-20 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-40 pointer-events-none noise-bg" />
          <div className="relative">
            <Rocket className="mx-auto text-primary mb-6" size={40} />
            <h2 className="font-display text-4xl sm:text-6xl font-bold text-foreground tracking-tight max-w-3xl mx-auto text-balance leading-[1.05]">
              The next decade of human progress belongs to communities.
            </h2>
            <p className="mt-6 text-muted-foreground max-w-xl mx-auto text-lg leading-relaxed">
              Investors, judges, neighbors — we invite you to help build the platform that turns kindness into infrastructure.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <Button
                onClick={() => navigate("/pitch")}
                data-testid="footer-cta-pitch"
                size="lg"
                className="rounded-full h-12 px-8 bg-primary hover:bg-primary/90"
              >
                View investor pitch <ArrowRight size={16} className="ml-1" />
              </Button>
              <Button
                onClick={() => navigate("/signup")}
                data-testid="footer-cta-signup"
                variant="outline"
                size="lg"
                className="rounded-full h-12 px-8 border-border"
              >
                Join the movement
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
